import { el } from './dom.js';

/**
 * Celebration effects.
 *
 * The brief was "cool effects that add to the excitement and don't distract",
 * which in practice means: never over the rack, never over the square the
 * player is about to tap, always shorter than a second, and always skipped
 * under prefers-reduced-motion. Everything is transform/opacity on a layer
 * that ignores pointer events.
 */
let layer: HTMLElement | null = null;

export function effectsLayer(parent: HTMLElement): HTMLElement {
  layer = el('div', { class: 'fx', 'aria-hidden': 'true' });
  parent.append(layer);
  return layer;
}

const reduced = (): boolean =>
  matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A small burst of sparks, used when a word completes. */
export function sparkle(x: number, y: number, colour = 'var(--accent)', count = 12): void {
  if (!layer || reduced()) return;
  for (let i = 0; i < count; i++) {
    const s = el('div', { class: 'spark' });
    s.style.background = colour;
    s.style.left = `${x}px`;
    s.style.top = `${y}px`;
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
    const dist = 26 + Math.random() * 44;
    const dur = 420 + Math.random() * 320;
    layer.append(s);
    s.animate(
      [
        { transform: 'translate(-50%,-50%) scale(1) rotate(0deg)', opacity: 1 },
        {
          transform: `translate(${Math.cos(angle) * dist - 50}%, ${Math.sin(angle) * dist + dist * 0.5 - 50}%) ` +
                     `scale(.2) rotate(${Math.random() * 300 - 150}deg)`,
          opacity: 0,
        },
      ],
      { duration: dur, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' },
    ).onfinish = () => s.remove();
  }
}

/** An expanding ring, used for a take-two call. */
export function ring(x: number, y: number, colour = 'var(--accent)'): void {
  if (!layer || reduced()) return;
  const r = el('div', { class: 'ring' });
  r.style.left = `${x}px`;
  r.style.top = `${y}px`;
  r.style.borderColor = colour;
  layer.append(r);
  setTimeout(() => r.remove(), 950);
}

/** The full-width headline for the big moments. */
export function sweep(text: string, colour = 'var(--accent)'): void {
  if (!layer) return;
  const s = el('div', { class: 'sweep', text });
  s.style.color = colour;
  layer.append(s);
  setTimeout(() => s.remove(), 1400);
}

/** Winner's confetti. Heavier than `sparkle`, used exactly once per match. */
export function confetti(): void {
  if (!layer || reduced()) return;
  const colours = ['#ffcc48', '#4ade80', '#60a5fa', '#f87171', '#a95cf2', '#f2a65a'];
  const w = layer.clientWidth;
  for (let i = 0; i < 90; i++) {
    const p = el('div', { class: 'spark' });
    p.style.background = colours[i % colours.length] as string;
    p.style.left = `${Math.random() * w}px`;
    p.style.top = '-12px';
    p.style.width = `${5 + Math.random() * 6}px`;
    p.style.height = `${8 + Math.random() * 8}px`;
    layer.append(p);
    p.animate(
      [
        { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
        { transform: `translateY(${(layer.clientHeight + 60)}px) rotate(${Math.random() * 900 - 450}deg)`,
          opacity: .1 },
      ],
      { duration: 1700 + Math.random() * 1600, delay: Math.random() * 500,
        easing: 'cubic-bezier(.3,.1,.5,1)', fill: 'forwards' },
    ).onfinish = () => p.remove();
  }
}
